import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Code,
  HStack,
  IconButton,
  GridItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import { FiCopy, FiExternalLink } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { ChainBadge } from '@/components/common/ChainBadge'
import type { ContractDeploymentRecord } from '@/types'
import { getExplorerAddressUrl, getExplorerTxUrl } from '@/constants/chains'
import { formatDateTime } from '@/utils/formatUtils'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'
import {
  getDeploymentDisplayAddresses,
  type DeploymentDisplayAddressKind,
} from '@/utils/adminAddressPresentation'

interface DeploymentDetailModalProps {
  isOpen: boolean
  onClose: () => void
  record: ContractDeploymentRecord | null
  onRefresh?: () => Promise<unknown> | void
  isRefreshing?: boolean
  borderColor: string
  secondaryTextColor: string
  tableCellColor: string
  modalBg: string
  resolveDeploymentChainId: (record: ContractDeploymentRecord) => string | number | null
  getDeploymentStatusColor: (record: ContractDeploymentRecord) => string
  getVerificationStatusColor: (statusId?: string | null) => string
  getVerificationStatusText: (statusId?: string | null) => string
  getInitializationStatusColor: (initialized?: boolean | null) => string
  getInitializationStatusText: (initialized?: boolean | null) => string
  getTerminalCleanupColor: (record: ContractDeploymentRecord) => string
  formatTerminalCleanupState: (record: ContractDeploymentRecord) => string
  getSettlementPreviewColor: (value?: boolean | null) => string
  formatSettlementPreviewStatus: (value?: boolean | null) => string
  formatGasCost: (record: ContractDeploymentRecord) => string
  copyToClipboard: (value: string) => void
}

const DeploymentDetailModal = ({
  isOpen,
  onClose,
  record,
  onRefresh,
  isRefreshing = false,
  borderColor,
  secondaryTextColor,
  tableCellColor,
  modalBg,
  resolveDeploymentChainId,
  getDeploymentStatusColor,
  getVerificationStatusColor,
  getVerificationStatusText,
  getInitializationStatusColor,
  getInitializationStatusText,
  getTerminalCleanupColor,
  formatTerminalCleanupState,
  getSettlementPreviewColor,
  formatSettlementPreviewStatus,
  formatGasCost,
  copyToClipboard,
}: DeploymentDetailModalProps) => {
  const { t } = useTranslation()
  const addressLabels: Record<DeploymentDisplayAddressKind, string> = {
    payment: t('contractRegistry.deployment.details.paymentAddress', 'Payment Address'),
    contract: t('contractRegistry.table.contractAddress', 'Contract Address'),
    predicted_contract: t('contractRegistry.deployment.details.predictedAddress'),
    deployed_contract: t('contractRegistry.deployment.details.deployedAddress'),
    vault_token_account: t(
      'contractRegistry.deployment.details.vaultTokenAccount',
      'Vault Token Account'
    ),
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW={{ base: 'calc(100vw - 24px)', xl: '1180px' }}>
        <ModalHeader pr={12}>
          <HStack justify="space-between" spacing={3}>
            <Text>{t('contractRegistry.deployment.details.title')}</Text>
            {onRefresh && (
              <RefreshIconButton
                label={t('common.refresh')}
                tooltipPlacement="left"
                onClick={() => void onRefresh()}
                isLoading={isRefreshing}
                isDisabled={isRefreshing}
                flexShrink={0}
              />
            )}
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody px={{ base: 4, md: 6 }} pb={6}>
          {record && (
            <VStack spacing={5} align="stretch">
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  textTransform="uppercase"
                  color={secondaryTextColor}
                  mb={3}
                >
                  Basic Information
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      textTransform="uppercase"
                      color={secondaryTextColor}
                      mb={1}
                    >
                      {t('contractRegistry.deployment.details.orderIdBusiness')}
                    </Text>
                    <Code wordBreak="break-all">{record.orderId || record.order_id}</Code>
                  </Box>
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      textTransform="uppercase"
                      color={secondaryTextColor}
                      mb={1}
                    >
                      {t('contractRegistry.deployment.details.registryId')}
                    </Text>
                    <Code fontSize="xs" wordBreak="break-all">
                      {record.registry_id}
                    </Code>
                  </Box>
                </SimpleGrid>
              </Box>

              <Box bg={modalBg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  textTransform="uppercase"
                  color={secondaryTextColor}
                  mb={3}
                  textAlign="center"
                >
                  Status Information
                </Text>
                <SimpleGrid columns={{ base: 1, sm: 2, md: 3, xl: 5 }} spacing={3}>
                  <VStack spacing={2} align="stretch" minW={0}>
                    <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color={secondaryTextColor}>
                      {t('contractRegistry.table.status')}
                    </Text>
                    <Badge colorScheme={getDeploymentStatusColor(record)} size="lg" px={3} py={1} alignSelf="flex-start">
                      {record.status_name || record.status_id}
                    </Badge>
                  </VStack>
                  <VStack spacing={2} align="stretch" minW={0}>
                    <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color={secondaryTextColor}>
                      {t('contractRegistry.deployment.table.chain')}
                    </Text>
                    <ChainBadge
                      chainId={resolveDeploymentChainId(record) || 'unknown'}
                      size="lg"
                      px={3}
                      py={1}
                      alignSelf="flex-start"
                    />
                  </VStack>
                  <VStack spacing={2} align="stretch" minW={0}>
                    <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color={secondaryTextColor}>
                      {t('contractRegistry.deployment.table.verification')}
                    </Text>
                    <Badge
                      colorScheme={getVerificationStatusColor(record.address_verification_status_id)}
                      size="lg"
                      px={3}
                      py={1}
                      alignSelf="flex-start"
                    >
                      {getVerificationStatusText(record.address_verification_status_id)}
                    </Badge>
                  </VStack>
                  <VStack spacing={2} align="stretch" minW={0}>
                    <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color={secondaryTextColor}>
                      {t('contractRegistry.deployment.table.initialization')}
                    </Text>
                    <Badge
                      colorScheme={getInitializationStatusColor(record.is_initialized)}
                      size="lg"
                      px={3}
                      py={1}
                      alignSelf="flex-start"
                    >
                      {getInitializationStatusText(record.is_initialized)}
                    </Badge>
                  </VStack>
                  <VStack spacing={2} align="stretch" minW={0}>
                    <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color={secondaryTextColor}>
                      {t('contractRegistry.deployment.details.terminalCleanupState')}
                    </Text>
                    <Badge
                      colorScheme={getTerminalCleanupColor(record)}
                      size="lg"
                      px={3}
                      py={1}
                      alignSelf="flex-start"
                    >
                      {formatTerminalCleanupState(record)}
                    </Badge>
                  </VStack>
                </SimpleGrid>
              </Box>

              <Box bg={modalBg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  textTransform="uppercase"
                  color={secondaryTextColor}
                  mb={3}
                >
                  {t('contractRegistry.deployment.details.settlementPreviewTitle')}
                </Text>
                <SimpleGrid columns={{ base: 1, lg: 4 }} spacing={4} alignItems="start">
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      textTransform="uppercase"
                      color={secondaryTextColor}
                      mb={1}
                    >
                      {t('contractRegistry.deployment.details.settlementPreviewStatus')}
                    </Text>
                    <Badge
                      colorScheme={getSettlementPreviewColor(record.is_pass_settlement_preview)}
                      size="lg"
                      px={3}
                      py={1}
                    >
                      {formatSettlementPreviewStatus(record.is_pass_settlement_preview)}
                    </Badge>
                  </Box>
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      textTransform="uppercase"
                      color={secondaryTextColor}
                      mb={1}
                    >
                      {t('contractRegistry.deployment.details.terminalCleanupRecordId')}
                    </Text>
                    <Code fontSize="xs" wordBreak="break-all">
                      {record.terminalCleanupEvidence?.recordId
                        || record.terminal_cleanup_record_id
                        || t('common.na')}
                    </Code>
                  </Box>
                  <GridItem colSpan={{ base: 1, lg: 2 }}>
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      textTransform="uppercase"
                      color={secondaryTextColor}
                      mb={1}
                    >
                      {t('contractRegistry.deployment.details.settlementPreviewNotes')}
                    </Text>
                    <Box borderRadius="md" borderWidth="1px" borderColor={borderColor} p={3} maxHeight="200px" overflowY="auto">
                      <Text fontSize="sm" color={tableCellColor} wordBreak="break-word" whiteSpace="pre-wrap">
                        {record.settlement_preview_notes?.trim() || t('common.na')}
                      </Text>
                    </Box>
                  </GridItem>
                </SimpleGrid>
              </Box>

              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  textTransform="uppercase"
                  color={secondaryTextColor}
                  mb={3}
                >
                  Address Information
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {getDeploymentDisplayAddresses(record).map(item => (
                    <Box minW={0} key={item.kind}>
                      <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        textTransform="uppercase"
                        color={secondaryTextColor}
                        mb={1}
                      >
                        {addressLabels[item.kind]}
                      </Text>
                      <HStack spacing={2}>
                        <Code flex="1" wordBreak="break-all">
                          {item.address}
                        </Code>
                        <Tooltip label={t('contractRegistry.actions.copyAddress')}>
                          <IconButton
                            aria-label="Copy"
                            icon={<FiCopy />}
                            size="sm"
                            variant="ghost"
                            onClick={() => copyToClipboard(item.address)}
                          />
                        </Tooltip>
                        <Tooltip label={t('contractRegistry.actions.viewOnExplorer')}>
                          <IconButton
                            aria-label="Explorer"
                            icon={<FiExternalLink />}
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const chainId = resolveDeploymentChainId(record) || 'unknown'
                              const url = getExplorerAddressUrl(chainId, item.address)
                              if (url) window.open(url, '_blank')
                            }}
                          />
                        </Tooltip>
                      </HStack>
                    </Box>
                  ))}
                </SimpleGrid>
                {record.address_mismatch && (
                  <Alert status="error" mt={4} borderRadius="md">
                    <AlertIcon />
                    <AlertDescription>
                      {t(
                        'contractRegistry.deployment.details.addressMismatch',
                        'The payment and deployed contract addresses do not match.'
                      )}
                    </AlertDescription>
                  </Alert>
                )}
              </Box>

              {record.deployment_tx_hash && (
                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    textTransform="uppercase"
                    color={secondaryTextColor}
                    mb={3}
                  >
                    Transaction Information
                  </Text>
                  <VStack spacing={3} align="stretch">
                    <Box>
                      <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        textTransform="uppercase"
                        color={secondaryTextColor}
                        mb={1}
                      >
                        {t('contractRegistry.deployment.details.txHash')}
                      </Text>
                      <HStack spacing={2}>
                        <Code flex="1" wordBreak="break-all">
                          {record.deployment_tx_hash}
                        </Code>
                        <Tooltip label={t('contractRegistry.actions.copyTxHash')}>
                          <IconButton
                            aria-label="Copy"
                            icon={<FiCopy />}
                            size="sm"
                            variant="ghost"
                            onClick={() => copyToClipboard(record.deployment_tx_hash!)}
                          />
                        </Tooltip>
                        <Tooltip label={t('contractRegistry.actions.viewOnExplorer')}>
                          <IconButton
                            aria-label="Explorer"
                            icon={<FiExternalLink />}
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const chainId = resolveDeploymentChainId(record) || 'unknown'
                              const url = getExplorerTxUrl(chainId, record.deployment_tx_hash!)
                              if (url) window.open(url, '_blank')
                            }}
                          />
                        </Tooltip>
                      </HStack>
                    </Box>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <Box minW={0}>
                        <Text
                          fontSize="xs"
                          fontWeight="semibold"
                          textTransform="uppercase"
                          color={secondaryTextColor}
                          mb={1}
                        >
                          {t('contractRegistry.deployment.table.gasCost')}
                        </Text>
                        <Text fontSize="sm" color={tableCellColor}>
                          {formatGasCost(record)}
                        </Text>
                      </Box>
                      <Box minW={0}>
                        <Text
                          fontSize="xs"
                          fontWeight="semibold"
                          textTransform="uppercase"
                          color={secondaryTextColor}
                          mb={1}
                        >
                          {t('contractRegistry.deployment.details.blockNumber')}
                        </Text>
                        <Text fontSize="sm" color={tableCellColor}>
                          {record.deployment_block_number || '-'}
                        </Text>
                      </Box>
                    </SimpleGrid>
                  </VStack>
                </Box>
              )}

              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  textTransform="uppercase"
                  color={secondaryTextColor}
                  mb={3}
                >
                  Timeline Information
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Box minW={0}>
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      textTransform="uppercase"
                      color={secondaryTextColor}
                      mb={1}
                    >
                      {t('contractRegistry.deployment.table.deployedAt')}
                    </Text>
                    <Text fontSize="sm" color={tableCellColor}>
                      {record.deployed_at
                        ? formatDateTime(record.deployed_at)
                        : t('contractRegistry.deployment.details.notDeployedYet')}
                    </Text>
                  </Box>
                  <Box minW={0}>
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      textTransform="uppercase"
                      color={secondaryTextColor}
                      mb={1}
                    >
                      {t('contractRegistry.deployment.details.createdAt')}
                    </Text>
                    <Text fontSize="sm" color={tableCellColor}>
                      {formatDateTime(record.created_at)}
                    </Text>
                  </Box>
                </SimpleGrid>
              </Box>

              {(record.deploy_notes ||
                record.initialization_notes ||
                record.verification_notes) && (
                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    textTransform="uppercase"
                    color={secondaryTextColor}
                    mb={3}
                  >
                    Notes
                  </Text>
                  <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={3}>
                    {record.deploy_notes && (
                      <Box>
                        <Text
                          fontSize="xs"
                          fontWeight="semibold"
                          textTransform="uppercase"
                          color={secondaryTextColor}
                          mb={1}
                        >
                          {t('contractRegistry.deployment.details.deploymentNotes')}
                        </Text>
                        <Box borderRadius="md" borderWidth="1px" borderColor={borderColor} p={3} maxHeight="200px" overflowY="auto">
                          <Text fontSize="sm" color={tableCellColor} wordBreak="break-word" whiteSpace="pre-wrap">
                            {record.deploy_notes}
                          </Text>
                        </Box>
                      </Box>
                    )}

                    {record.verification_notes && (
                      <Box>
                        <Text
                          fontSize="xs"
                          fontWeight="semibold"
                          textTransform="uppercase"
                          color={secondaryTextColor}
                          mb={1}
                        >
                          {t('contractRegistry.deployment.details.verificationNotes')}
                        </Text>
                        <Box borderRadius="md" borderWidth="1px" borderColor={borderColor} p={3} maxHeight="200px" overflowY="auto">
                          <Text fontSize="sm" color={tableCellColor} wordBreak="break-word" whiteSpace="pre-wrap">
                            {record.verification_notes}
                          </Text>
                        </Box>
                      </Box>
                    )}

                    {record.initialization_notes && (
                      <Box>
                        <Text
                          fontSize="xs"
                          fontWeight="semibold"
                          textTransform="uppercase"
                          color={secondaryTextColor}
                          mb={1}
                        >
                          {t('contractRegistry.deployment.details.initializationNotes')}
                        </Text>
                        <Box borderRadius="md" borderWidth="1px" borderColor={borderColor} p={3} maxHeight="200px" overflowY="auto">
                          <Text fontSize="sm" color={tableCellColor} wordBreak="break-word" whiteSpace="pre-wrap">
                            {record.initialization_notes}
                          </Text>
                        </Box>
                      </Box>
                    )}
                  </SimpleGrid>
                </Box>
              )}
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default DeploymentDetailModal
